import queryString, { StringifiableRecord } from 'query-string';

import {
  ActivityKindEnum,
  AdFieldBathsEnum,
  AdFieldBedsEnum,
  AdFieldFloorEnum,
  AdFieldFurnitureEnum,
  AdFieldOccupancyEnum,
  AdFieldRenovationEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  SortingOrderEnum,
  TransactionTypeEnum,
  UserKindEnum,
} from '../../../types/enums';
import { AdsFilteringType, AdsSortingType } from '../../../types/ads';
import { PaginationType } from '../../../types/pagination';

export type AdsSearchParamsSource = string | Record<string, unknown>;
export type TopAgentFilterValue = 'all' | string | undefined;

export interface AdsSearchParams {
  addressParamAddress?: string;
  addressParamLat?: string;
  addressParamLong?: string;
  addressParamName?: string;
  addressParamPlaceId?: string;
  areaMax?: number;
  areaMin?: number;
  baths?: AdFieldBathsEnum[];
  beds?: AdFieldBedsEnum[];
  completionStatus?: CompletionStatusEnum[];
  'creator.id'?: string;
  'creator.kind'?: UserKindEnum;
  'creator.name'?: string;
  downPayment?: number;
  field?: string;
  floor?: AdFieldFloorEnum[];
  floorsNumFrom?: number;
  floorsNumTo?: number;
  furniture?: AdFieldFurnitureEnum[];
  'handover.quarter'?: string;
  'handover.year'?: string;
  isMyAgency?: boolean;
  keywords?: string;
  locationId?: string[];
  multiHandover?: string[];
  myFriends?: boolean;
  occupancy?: AdFieldOccupancyEnum[];
  order?: SortingOrderEnum;
  page: number;
  postHandover?: boolean;
  priceMax?: number;
  priceMin?: number;
  propertyKind?: PropertyKindEnum[];
  renovation?: AdFieldRenovationEnum[];
  rentFrequency?: RentPeriodEnum;
  sort?: string;
  transactionType?: TransactionTypeEnum[];
  topAgent?: TopAgentFilterValue;
}

const getSourceObject = (source: AdsSearchParamsSource): Record<string, unknown> =>
  typeof source === 'string' ? queryString.parse(source) : source;

const getScalarString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    for (const item of value) {
      const scalarValue = getScalarString(item);
      if (scalarValue !== undefined) return scalarValue;
    }
    return undefined;
  }

  if (value === null || value === undefined) return undefined;

  const normalizedValue = String(value).trim();
  return normalizedValue || undefined;
};

const getStringArray = (value: unknown): string[] | undefined => {
  const normalizedValues: string[] = [];

  const appendValue = (currentValue: unknown) => {
    if (Array.isArray(currentValue)) {
      currentValue.forEach(appendValue);
      return;
    }

    if (currentValue === null || currentValue === undefined) return;

    String(currentValue)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => normalizedValues.push(item));
  };

  appendValue(value);
  return normalizedValues.length ? normalizedValues : undefined;
};

const isAllowedValue = <T extends string>(value: string, allowedValues: readonly T[]): value is T =>
  allowedValues.some((allowedValue) => allowedValue === value);

const getAllowedStringArray = <T extends string>(
  value: unknown,
  allowedValues: readonly T[]
): T[] | undefined => {
  const normalizedValues = getStringArray(value)?.filter((item): item is T =>
    isAllowedValue(item, allowedValues)
  );

  return normalizedValues?.length ? normalizedValues : undefined;
};

const getAllowedScalarString = <T extends string>(
  value: unknown,
  allowedValues: readonly T[]
): T | undefined => {
  const normalizedValue = getScalarString(value);

  return normalizedValue && isAllowedValue(normalizedValue, allowedValues)
    ? normalizedValue
    : undefined;
};

const getNumber = (value: unknown): number | undefined => {
  const stringValue = getScalarString(value);
  if (stringValue === undefined) return undefined;

  const normalizedValue = Number(stringValue);
  return Number.isFinite(normalizedValue) && normalizedValue !== 0 ? normalizedValue : undefined;
};

const getBoolean = (value: unknown): boolean | undefined => {
  const stringValue = getScalarString(value)?.toLowerCase();
  if (stringValue === 'true') return true;
  if (stringValue === 'false') return false;
  return undefined;
};

const getPage = (value: unknown): number => {
  const stringValue = getScalarString(value);
  if (stringValue === undefined) return 1;

  const normalizedValue = Number(stringValue);
  return Number.isInteger(normalizedValue) && normalizedValue >= 1 ? normalizedValue : 1;
};

const getTopAgent = (value: unknown): TopAgentFilterValue => {
  const normalizedValue = getScalarString(value);

  return normalizedValue === 'all' || /^[1-9]\d*$/.test(normalizedValue ?? '')
    ? normalizedValue
    : undefined;
};

const getSortingOrder = (value: unknown): SortingOrderEnum | undefined => {
  const stringValue = getScalarString(value);
  if (stringValue === SortingOrderEnum.Asc) return SortingOrderEnum.Asc;
  if (stringValue === SortingOrderEnum.Desc) return SortingOrderEnum.Desc;
  return undefined;
};

export const parseAdsSearchParams = (source: AdsSearchParamsSource): AdsSearchParams => {
  const sourceObject = getSourceObject(source);
  const completionStatus = getAllowedStringArray(
    sourceObject.completionStatus,
    Object.values(CompletionStatusEnum)
  )?.filter((value) => value !== CompletionStatusEnum.All);

  return {
    addressParamAddress: getScalarString(sourceObject.addressParamAddress),
    addressParamLat: getScalarString(sourceObject.addressParamLat),
    addressParamLong: getScalarString(sourceObject.addressParamLong),
    addressParamName: getScalarString(sourceObject.addressParamName),
    addressParamPlaceId: getScalarString(sourceObject.addressParamPlaceId),
    areaMax: getNumber(sourceObject.areaMax),
    areaMin: getNumber(sourceObject.areaMin),
    baths: getAllowedStringArray(sourceObject.baths, Object.values(AdFieldBathsEnum)),
    beds: getAllowedStringArray(sourceObject.beds, Object.values(AdFieldBedsEnum)),
    completionStatus: completionStatus?.length ? completionStatus : undefined,
    'creator.id': getScalarString(sourceObject['creator.id']),
    'creator.kind': getAllowedScalarString(
      sourceObject['creator.kind'],
      Object.values(UserKindEnum)
    ),
    'creator.name': getScalarString(sourceObject['creator.name']),
    downPayment: getNumber(sourceObject.downPayment),
    field: getScalarString(sourceObject.field),
    floor: getAllowedStringArray(sourceObject.floor, Object.values(AdFieldFloorEnum)),
    floorsNumFrom: getNumber(sourceObject.floorsNumFrom),
    floorsNumTo: getNumber(sourceObject.floorsNumTo),
    furniture: getAllowedStringArray(sourceObject.furniture, Object.values(AdFieldFurnitureEnum)),
    'handover.quarter': getScalarString(sourceObject['handover.quarter']),
    'handover.year': getScalarString(sourceObject['handover.year']),
    isMyAgency: getBoolean(sourceObject.isMyAgency),
    keywords: getScalarString(sourceObject.keywords),
    locationId: getStringArray(sourceObject.locationId),
    multiHandover: getStringArray(sourceObject.multiHandover),
    myFriends: getBoolean(sourceObject.myFriends),
    occupancy: getAllowedStringArray(sourceObject.occupancy, Object.values(AdFieldOccupancyEnum)),
    order: getSortingOrder(sourceObject.order),
    page: getPage(sourceObject.page),
    postHandover: getBoolean(sourceObject.postHandover),
    priceMax: getNumber(sourceObject.priceMax),
    priceMin: getNumber(sourceObject.priceMin),
    propertyKind: getAllowedStringArray(sourceObject.propertyKind, Object.values(PropertyKindEnum)),
    renovation: getAllowedStringArray(
      sourceObject.renovation,
      Object.values(AdFieldRenovationEnum)
    ),
    rentFrequency: getAllowedScalarString(
      sourceObject.rentFrequency,
      Object.values(RentPeriodEnum)
    ),
    sort: getScalarString(sourceObject.sort),
    transactionType: getAllowedStringArray(
      sourceObject.transactionType,
      Object.values(TransactionTypeEnum)
    ),
    topAgent: getTopAgent(sourceObject.topAgent),
  };
};

export const getAdsSearchQueryParams = (source: AdsSearchParamsSource): StringifiableRecord => {
  const params = parseAdsSearchParams(source);
  const hasLocationId = !!params.locationId?.length;

  return {
    addressParamAddress: params.addressParamAddress,
    addressParamLat: hasLocationId ? undefined : params.addressParamLat,
    addressParamLong: hasLocationId ? undefined : params.addressParamLong,
    addressParamName: params.addressParamName,
    addressParamPlaceId: hasLocationId ? undefined : params.addressParamPlaceId,
    areaMax: params.areaMax,
    areaMin: params.areaMin,
    baths: params.baths,
    beds: params.beds,
    completionStatus: params.completionStatus,
    'creator.id': params['creator.id'],
    'creator.kind': params['creator.kind'],
    'creator.name': params['creator.name'],
    downPayment: params.downPayment,
    field: params.field,
    floor: params.floor,
    floorsNumFrom: params.floorsNumFrom,
    floorsNumTo: params.floorsNumTo,
    furniture: params.furniture,
    'handover.quarter': params['handover.quarter'],
    'handover.year': params['handover.year'],
    isMyAgency: params.isMyAgency,
    keywords: params.keywords,
    locationId: params.locationId,
    multiHandover: params.multiHandover,
    myFriends: params.myFriends === true ? true : undefined,
    occupancy: params.occupancy,
    order: params.order,
    page: params.page,
    postHandover: params.postHandover,
    priceMax: params.priceMax,
    priceMin: params.priceMin,
    propertyKind: params.propertyKind,
    renovation: params.renovation,
    rentFrequency: params.rentFrequency,
    sort: params.sort,
    transactionType: params.transactionType,
    topAgent: params.topAgent,
  };
};

export const stringifyAdsSearchParams = (source: AdsSearchParamsSource): string =>
  queryString.stringify(getAdsSearchQueryParams(source), {
    skipEmptyString: true,
    skipNull: true,
  });

export const getAdsApiFilter = (params: AdsSearchParams, isRent: boolean): AdsFilteringType => {
  const hasLocationId = !!params.locationId?.length;
  const address =
    !hasLocationId && params.addressParamLat && params.addressParamLong
      ? [
          {
            addressEn: params.addressParamAddress,
            coordinatesLat: params.addressParamLat,
            coordinatesLong: params.addressParamLong,
            nameEn: params.addressParamName,
            placeId: params.addressParamPlaceId,
          },
        ]
      : [];
  const hasCreator = params['creator.name'] && params['creator.kind'] && params['creator.id'];
  const hasHandover = params['handover.quarter'] || params['handover.year'];

  return {
    address,
    activityKind: isRent ? ActivityKindEnum.Rent : ActivityKindEnum.Sale,
    transactionType: params.transactionType,
    rentFrequency: params.rentFrequency,
    propertyKind: params.propertyKind,
    beds: params.beds,
    areaMin: params.areaMin,
    areaMax: params.areaMax,
    baths: params.baths,
    priceMin: params.priceMin,
    priceMax: params.priceMax,
    locationId: params.locationId,
    status: undefined,
    completionStatus: params.completionStatus,
    creator: hasCreator
      ? {
          name: params['creator.name'],
          kind: params['creator.kind'],
          id: params['creator.id'],
        }
      : undefined,
    postHandover: params.postHandover,
    handover: hasHandover
      ? {
          quarter: params['handover.quarter'],
          year: params['handover.year'],
        }
      : undefined,
    multiHandover: params.multiHandover,
    downPayment: params.downPayment,
    furniture: params.furniture,
    renovation: params.renovation,
    floor: params.floor,
    occupancy: params.occupancy,
    floorsNumFrom: params.floorsNumFrom,
    floorsNumTo: params.floorsNumTo,
    keywords: params.keywords,
    isMyAgency: params.isMyAgency,
    myFriends: params.myFriends === true ? true : undefined,
  };
};

export const getAdsSorting = (params: AdsSearchParams, defaultField: string): AdsSortingType => ({
  field: params.field || params.sort || defaultField,
  order: params.order || SortingOrderEnum.Desc,
});

export const getAdsPagination = (params: AdsSearchParams, limit: number): PaginationType => ({
  page: params.page,
  offset: (params.page - 1) * limit,
  limit,
  total: 0,
});

export const getAdsPageWithinBounds = (page: number, total: number, limit: number): number => {
  const normalizedPage = Number.isInteger(page) && page >= 1 ? page : 1;
  const normalizedTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const normalizedLimit = Number.isFinite(limit) && limit > 0 ? limit : 1;
  const lastPage = Math.max(1, Math.ceil(normalizedTotal / normalizedLimit));

  return Math.min(normalizedPage, lastPage);
};

export const getAdsPageWithinPageCount = (page: number, pageCount: number): number => {
  const normalizedPage = Number.isInteger(page) && page >= 1 ? page : 1;
  const lastPage = Number.isInteger(pageCount) && pageCount > 0 ? pageCount : 1;

  return Math.min(normalizedPage, lastPage);
};
